import { Component, Inject, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Socio } from '../account/account.dto';
import { ConnectorService } from '../connector.service';
import { environment } from '../../environments/environment';
import { buildQrContent } from '../utils/hmac.util';

export interface SocioCardDialogData {
  socios: Socio[];
  compteId: number;
}

interface SocioPass {
  socios: {
    socioId: number;
    memberSince: number | null;
    adhesion: { campaign: string; endDate: string } | null;
  }[];
  renewal: { slug: string; name: string } | null;
}

/** En dessous de ce nombre de jours, on prévient que l'adhésion arrive à échéance. */
const EXPIRY_WARNING_DAYS = 30;

@Component({
  selector: 'app-socio-card-dialog',
  templateUrl: './socio-card-dialog.component.html',
  styleUrls: ['./socio-card-dialog.component.scss']
})
export class SocioCardDialogComponent implements OnInit {
  selectedSocio: Socio | null = null;
  qrData: string = '';
  fullscreen = false;
  pass: SocioPass | null = null;

  constructor(
    public dialogRef: MatDialogRef<SocioCardDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SocioCardDialogData,
    private http: HttpClient,
    private connector: ConnectorService,
  ) {}

  ngOnInit(): void {
    if (this.data.socios.length > 0) {
      // Sélectionner le socio avec le plus petit ID par défaut
      this.selectedSocio = this.data.socios.reduce(
        (min: Socio, s: Socio) => s.id < min.id ? s : min,
        this.data.socios[0]
      );
      this.updateQrData();
    }
    // Ancienneté et lien de renouvellement : la carte s'affiche sans, ils complètent quand ils arrivent
    this.http.get<SocioPass>(`${environment.backAPI}/socio-pass`, {
      headers: { 'Authorization': 'Bearer ' + this.connector.token() },
    }).subscribe({ next: (pass) => this.pass = pass, error: () => this.pass = null });
  }

  selectSocio(socio: Socio): void {
    this.selectedSocio = socio;
    this.updateQrData();
  }

  private async updateQrData(): Promise<void> {
    if (this.selectedSocio) {
      const identifier = `${this.selectedSocio.id}:${this.data.compteId}`;
      this.qrData = await buildQrContent(identifier);
    }
  }

  /**
   * Première lettre de chaque mot en majuscule, le reste en minuscules : « DARRICARRÈRE » →
   * « Darricarrère », « jean-pierre » → « Jean-Pierre », « d'artagnan » → « D'Artagnan ».
   * \p{L} reconnaît les lettres accentuées (\b\w les prenait pour des séparateurs).
   */
  formatName(name: string): string {
    if (!name) return '';
    return name
      .trim()
      .toLocaleLowerCase('fr')
      .replace(/(^|[\s\-'’])(\p{L})/gu, (_: string, sep: string, letter: string) => sep + letter.toLocaleUpperCase('fr'));
  }

  get isSocio(): boolean {
    return !!this.selectedSocio?.techId;
  }

  private get passEntry(): SocioPass['socios'][number] | undefined {
    return this.pass?.socios.find((s) => s.socioId === this.selectedSocio?.id);
  }

  /** Dernière adhésion par campagne ; sans campagne, la personne est simplement « non adhérente » */
  get adhesion(): { campaign: string; endDate: string } | null {
    return this.passEntry?.adhesion ?? null;
  }

  get endDate(): Date | null {
    return this.adhesion ? new Date(this.adhesion.endDate + 'T23:59:59') : null;
  }

  /** Jours restants avant la fin de l'adhésion fixée par la campagne */
  get daysLeft(): number | null {
    return this.endDate ? Math.ceil((this.endDate.getTime() - Date.now()) / 86_400_000) : null;
  }

  get isAdherent(): boolean {
    return this.daysLeft !== null && this.daysLeft >= 0;
  }

  get expiresSoon(): boolean {
    return this.isAdherent && this.daysLeft! <= EXPIRY_WARNING_DAYS;
  }

  /** Couleur de la carte : moutarde (socio et adhérent), bleu roi (socio), bleu ciel (adhérent), gris-bleu (aucun) */
  get variant(): 'gold' | 'royal' | 'sky' | 'none' {
    if (this.isSocio) return this.isAdherent ? 'gold' : 'royal';
    return this.isAdherent ? 'sky' : 'none';
  }

  /**
   * Saison de la dernière adhésion par campagne, au format « 26/27 » : celle écrite dans le
   * nom de la campagne (« Adhésion 2026-2027 »), à défaut celle qui se termine l'année de la
   * fin d'adhésion (une adhésion 26-27 peut courir jusqu'à l'automne 2027).
   */
  get seasonShort(): string | null {
    if (!this.adhesion) return null;
    const yy = (year: number) => String(year % 100).padStart(2, '0');
    const named = /(?:20)?(\d{2})\s*[-/–]\s*(?:20)?(\d{2})/.exec(this.adhesion.campaign ?? '');
    if (named && Number(named[2]) === (Number(named[1]) + 1) % 100) {
      return `${named[1]}/${named[2]}`;
    }
    const endYear = this.endDate?.getFullYear();
    return endYear ? `${yy(endYear - 1)}/${yy(endYear)}` : null;
  }

  get memberSince(): number | null {
    return this.passEntry?.memberSince ?? null;
  }

  /** Le bouton « Renouveler » n'apparaît que si une campagne d'adhésion est ouverte et que l'adhésion est échue ou proche de l'être. */
  get renewal(): SocioPass['renewal'] {
    return this.pass && (!this.isAdherent || this.expiresSoon) ? this.pass.renewal : null;
  }

  get qrWidth(): number {
    return this.fullscreen ? Math.min(window.innerWidth, window.innerHeight) - 64 : 160;
  }

  toggleFullscreen(): void {
    this.fullscreen = !this.fullscreen;
    if (this.fullscreen) this.dialogRef.addPanelClass('socio-card-dialog--fullscreen');
    else this.dialogRef.removePanelClass('socio-card-dialog--fullscreen');
  }

  close(): void {
    this.dialogRef.close();
  }
}
